/* eslint-disable max-len */
import {
  DyslexiaScreenerListDisplayRowModel,
  DYSLEXIA_SCREENER_LIST_ROW_TYPE_GRADE_1,
  DYSLEXIA_SCREENER_LIST_ROW_TYPE_GRADE_KG,
  DYSLEXIA_SCREENER_LIST_ROW_TYPE_TRAINING,
  DYSLEXIA_SCREENER_LIST_STATUS_COMPLETE,
  DYSLEXIA_SCREENER_LIST_STATUS_NOT_STARTED,
} from '../types/DyslexiaScreenerListDisplayRowModel';
import { DyslexiaScreenerListItemModel } from '../types/DyslexiaScreenerListItemModel';

const isPresentString = (v: unknown): boolean =>
  v != null && String(v).trim() !== '';

/** API record id for dyslexia screener detail; empty means create-new flow. */
export const dyslexiaScreenerListItemRecordId = (
  id: DyslexiaScreenerListItemModel['id']
): string => (id != null ? String(id).trim() : '');

const isPresentNumber = (v: unknown): boolean =>
  v != null && typeof v === 'number' && !Number.isNaN(v);

const isPresentBoolean = (v: unknown): boolean => typeof v === 'boolean';

const asOptionalNumber = (v: unknown): number | undefined => {
  if (v === null || v === undefined) return undefined;
  if (typeof v === 'number' && !Number.isNaN(v)) return v;
  if (typeof v === 'string' && String(v).trim() !== '') {
    const n = Number(v);
    return Number.isNaN(n) ? undefined : n;
  }
  return undefined;
};

const firstDefinedNumber = (...vals: unknown[]): number => {
  for (const v of vals) {
    const n = asOptionalNumber(v);
    if (n !== undefined) return n;
  }
  return 0;
};

const rawItem = (item: DyslexiaScreenerListItemModel, key: string): unknown =>
  (item as unknown as Record<string, unknown>)[key];

/** Coerce list API JSON casing (e.g. kgiEPStudentCount) into typed list item fields. */
const normalizeListItemNumericFields = (
  item: DyslexiaScreenerListItemModel
): DyslexiaScreenerListItemModel => ({
  ...item,
  kgIEPStudentCount: firstDefinedNumber(
    item.kgIEPStudentCount,
    rawItem(item, 'kgiepStudentCount')
  ),
  grade1IEPStudentCount: firstDefinedNumber(
    item.grade1IEPStudentCount,
    rawItem(item, 'grade1IEPStudentCount')
  ),
  kg504StudentCount: firstDefinedNumber(
    item.kg504StudentCount,
    rawItem(item, 'kG504StudentCount')
  ),
  grade1504StudentCount: firstDefinedNumber(
    item.grade1504StudentCount,
    rawItem(item, 'grade1504StudentCount')
  ),
});

const isGrade1ScreenerComplete = (
  item: DyslexiaScreenerListItemModel
): boolean =>
  [
    isPresentString(item.grade1ScreenerUsed),
    isPresentString(item.grade1ScreenerCompletionDate),
    isPresentNumber(item.grade1ScreenerCount),
    isPresentNumber(item.grade1ScreenerFailedOrAtRiskCount),
    isPresentBoolean(item.grade1ParentsNotified),
    isPresentNumber(item.grade1IEPStudentCount),
    isPresentNumber(item.grade1504StudentCount),
  ].every(Boolean);

const isGradeKgScreenerComplete = (
  item: DyslexiaScreenerListItemModel
): boolean =>
  [
    isPresentString(item.kgScreenerUsed),
    isPresentString(item.kgScreenerCompletionDate),
    isPresentNumber(item.kgScreenerCount),
    isPresentNumber(item.kgScreenerFailedOrAtRiskCount),
    isPresentBoolean(item.kgParentsNotified),
    isPresentNumber(item.kgIEPStudentCount),
    isPresentNumber(item.kg504StudentCount),
  ].every(Boolean);

/** List "Awareness Training Cycle" row: Complete iff Training Acknowledgement Date is set. */
const isAwarenessTrainingComplete = (
  item: DyslexiaScreenerListItemModel
): boolean => isPresentString(item.trainingAcknowledgementDate);

const toStatus = (
  complete: boolean
):
  | typeof DYSLEXIA_SCREENER_LIST_STATUS_COMPLETE
  | typeof DYSLEXIA_SCREENER_LIST_STATUS_NOT_STARTED =>
  complete
    ? DYSLEXIA_SCREENER_LIST_STATUS_COMPLETE
    : DYSLEXIA_SCREENER_LIST_STATUS_NOT_STARTED;

const isListItemGrade1RowEnabled = (
  item: DyslexiaScreenerListItemModel
): boolean =>
  item.grade1Exists !== false && rawItem(item, 'grade1Exists') !== false;

const isListItemKgRowEnabled = (item: DyslexiaScreenerListItemModel): boolean =>
  item.kgExists !== false && rawItem(item, 'kgExists') !== false;

const buildRow = (
  item: DyslexiaScreenerListItemModel,
  suffix: string,
  type: DyslexiaScreenerListDisplayRowModel['type'],
  complete: boolean,
  dyslexiaScreenerUpdate: boolean
): DyslexiaScreenerListDisplayRowModel => {
  const recordId = dyslexiaScreenerListItemRecordId(item.id);
  const rowKey =
    recordId ||
    `${item.leaIdentifier ?? ''}::${item.schoolIdentifier ?? ''}::${
      item.schoolYear
    }`;
  return {
    rowId: `${rowKey}::${suffix}`,
    recordId,
    schoolYear: item.schoolYear,
    districtName: item.districtName ?? '',
    leaIdentifier: item.leaIdentifier ?? '',
    schoolName: item.schoolName ?? '',
    schoolIdentifier: item.schoolIdentifier ?? '',
    kgIEPStudentCount: item.kgIEPStudentCount,
    grade1IEPStudentCount: item.grade1IEPStudentCount,
    kg504StudentCount: item.kg504StudentCount,
    grade1504StudentCount: item.grade1504StudentCount,
    kgExists: item.kgExists,
    grade1Exists: item.grade1Exists,
    type,
    status: toStatus(complete),
    dyslexiaScreenerUpdate,
  };
};

export const expandDyslexiaScreenerListItemsToDisplayRows = (
  items: DyslexiaScreenerListItemModel[],
  dyslexiaScreenerUpdate = false
): DyslexiaScreenerListDisplayRowModel[] => {
  const rows: DyslexiaScreenerListDisplayRowModel[] = [];
  for (const item of items) {
    const normalized = normalizeListItemNumericFields(item);
    if (isListItemGrade1RowEnabled(normalized)) {
      rows.push(
        buildRow(
          normalized,
          'grade1',
          DYSLEXIA_SCREENER_LIST_ROW_TYPE_GRADE_1,
          isGrade1ScreenerComplete(normalized),
          dyslexiaScreenerUpdate
        )
      );
    }
    if (isListItemKgRowEnabled(normalized)) {
      rows.push(
        buildRow(
          normalized,
          'gradeKg',
          DYSLEXIA_SCREENER_LIST_ROW_TYPE_GRADE_KG,
          isGradeKgScreenerComplete(normalized),
          dyslexiaScreenerUpdate
        )
      );
    }
    rows.push(
      buildRow(
        normalized,
        'training',
        DYSLEXIA_SCREENER_LIST_ROW_TYPE_TRAINING,
        isAwarenessTrainingComplete(normalized),
        dyslexiaScreenerUpdate
      )
    );
  }
  return rows;
};
